// Sign-in, invitations, members, organisations (GIOTHUB-185, GIOTHUB-186).
import type { Dict } from "./dict";

const en = {
  "members.account.unknown": "The gIoTHub account of this address could not be checked: if the person has none yet, ask Gimasi to create it.",
  "members.account.created": "A gIoTHub account was created for this address: gIoTHub e-mails the person how to set it up.",
  "login.drawing": "Sample drawing of a device board",
  "login.sample": "Sample drawing",
  "login.tb.type": "Device type",
  "login.tb.version": "Version",
  "login.tb.drawing": "Drawing",
  "login.tb.scale": "Scale",
  "login.lead": "Build device types with AI assistance: codec, catalogue, guide and tests, checked by the gIoT Store's own engine.",
  "login.email": "E-mail address",
  "login.go": "Continue",
  "login.bad": "Enter your e-mail address.",
  "login.note": "Invited by your company? Open the link in the invitation e-mail: it brings you here and adds you to your organisation. If you have no gIoTHub account yet, a separate e-mail from gIoTHub lets you set it up.",
  "login.demo": "Demo",
  "noorg.title": "No organisation yet",
  "noorg.text": "You are signed in as {email}, but you are not a member of an organisation in the Type Builder yet.",
  "noorg.invite": "Ask an administrator of your company to invite this e-mail address. The invitation e-mail has a link that brings you in.",
  "noorg.disabled": "Building is switched off for your organisation at the moment.",
  "invite.unknown": "This invitation link is not valid.",
  "invite.lead": "You are invited to join {org} in the gIoTHub Device Type Builder as {role}.",
  "invite.accepted": "This invitation has already been accepted. Sign in to continue.",
  "invite.expired": "This invitation has expired. Ask for a new one.",
  "invite.revoked": "This invitation was withdrawn.",
  "invite.signin": "Sign in with {email} to accept.",
  "invite.title": "Join {org}",
  "invite.from": "Invited by {who}.",
  "invite.until": "The link works until {date}.",
  "invite.wrong": "You are signed in as {email}, but the invitation is for {invited}. Sign out and sign in with the invited address.",
  "invite.unverified": "Your e-mail address is not verified in your gIoTHub account yet: verify it first.",
  "invite.accept": "Accept the invitation",
  "members.title": "Members of {org}",
  "members.leave": "Leave the organisation",
  "members.leave.confirm": "Leave {org}? You lose access to its projects.",
  "members.person": "Person",
  "members.role": "Role",
  "members.you": "you",
  "members.changed": "Role changed.",
  "members.remove.confirm": "Remove {who} from the organisation? Their projects stay.",
  "members.removed": "Removed.",
  "members.remove": "Remove",
  "members.roles": "Administrators invite people and manage roles; builders create and edit projects and publish versions; viewers read.",
  "members.open": "Open invitations",
  "members.open.none": "No open invitations.",
  "members.until": "until {date}",
  "members.withdrawn": "Invitation withdrawn.",
  "members.withdraw": "Withdraw",
  "members.invite": "Invite someone",
  "members.email": "E-mail address",
  "members.lang": "Language of the e-mail",
  "members.send": "Send the invitation",
  "members.mail.sent": "The invitation was sent to {email}.",
  "members.mail.not": "The e-mail was not sent: pass the link on yourself.",
  "members.link": "Invitation link (valid 7 days):",
  "ops.reinvite.prompt": "E-mail address of the administrator to invite:",
  "ops.title": "Organisations",
  "ops.text": "Every organisation in the builder. A new organisation gets its first administrator by invitation; its administrators invite the others. The console's Builder tab does the same.",
  "ops.operator": "operator",
  "ops.on": "building on",
  "ops.off": "switched off",
  "ops.usage": "{projects} project(s)",
  "ops.reinvite": "Invite an administrator",
  "ops.switchoff": "Switch off",
  "ops.switchon": "Switch on",
  "ops.nomembers": "No members yet.",
  "ops.invited": "invited: {email} ({role}, until {date})",
  "ops.new": "New organisation",
  "ops.new.text": "Creates the organisation and invites its first administrator.",
  "ops.name": "Name",
  "ops.admin": "First administrator (e-mail)",
  "ops.create": "Create and invite",
} as const;

const de = {
  "members.account.unknown": "Das gIoTHub-Konto dieser Adresse konnte nicht geprüft werden: Hat die Person noch keines, bitten Sie Gimasi, es zu erstellen.",
  "members.account.created": "Für diese Adresse wurde ein gIoTHub-Konto erstellt: gIoTHub schreibt der Person, wie sie es einrichtet.",
  "login.drawing": "Beispielzeichnung einer Geräteplatine",
  "login.sample": "Beispielzeichnung",
  "login.tb.type": "Gerätetyp",
  "login.tb.version": "Version",
  "login.tb.drawing": "Zeichnung",
  "login.tb.scale": "Massstab",
  "login.lead": "Gerätetypen mit KI-Unterstützung erstellen: Codec, Katalog, Leitfaden und Tests, geprüft mit der Engine des gIoT Store.",
  "login.email": "E-Mail-Adresse",
  "login.go": "Weiter",
  "login.bad": "Geben Sie Ihre E-Mail-Adresse ein.",
  "login.note": "Von Ihrer Firma eingeladen? Öffnen Sie den Link in der Einladung: Er führt Sie hierher und fügt Sie Ihrer Organisation hinzu. Haben Sie noch kein gIoTHub-Konto, erhalten Sie eine separate E-Mail von gIoTHub, um es einzurichten.",
  "login.demo": "Demo",
  "noorg.title": "Noch keine Organisation",
  "noorg.text": "Sie sind als {email} angemeldet, aber noch nicht Mitglied einer Organisation im Type Builder.",
  "noorg.invite": "Bitten Sie eine Administratorin oder einen Administrator Ihrer Firma, diese E-Mail-Adresse einzuladen. Die Einladung enthält einen Link, der Sie hereinbringt.",
  "noorg.disabled": "Für Ihre Organisation ist das Erstellen zurzeit ausgeschaltet.",
  "invite.unknown": "Dieser Einladungslink ist nicht gültig.",
  "invite.lead": "Sie sind eingeladen, {org} im gIoTHub Device Type Builder als {role} beizutreten.",
  "invite.accepted": "Diese Einladung wurde bereits angenommen. Melden Sie sich an, um fortzufahren.",
  "invite.expired": "Diese Einladung ist abgelaufen. Bitten Sie um eine neue.",
  "invite.revoked": "Diese Einladung wurde zurückgezogen.",
  "invite.signin": "Melden Sie sich mit {email} an, um sie anzunehmen.",
  "invite.title": "{org} beitreten",
  "invite.from": "Eingeladen von {who}.",
  "invite.until": "Der Link ist gültig bis {date}.",
  "invite.wrong": "Sie sind als {email} angemeldet, die Einladung gilt aber für {invited}. Melden Sie sich ab und mit der eingeladenen Adresse wieder an.",
  "invite.unverified": "Ihre E-Mail-Adresse ist in Ihrem gIoTHub-Konto noch nicht bestätigt: Bestätigen Sie sie zuerst.",
  "invite.accept": "Einladung annehmen",
  "members.title": "Mitglieder von {org}",
  "members.leave": "Organisation verlassen",
  "members.leave.confirm": "{org} verlassen? Sie verlieren den Zugang zu deren Projekten.",
  "members.person": "Person",
  "members.role": "Rolle",
  "members.you": "Sie",
  "members.changed": "Rolle geändert.",
  "members.remove.confirm": "{who} aus der Organisation entfernen? Die Projekte bleiben erhalten.",
  "members.removed": "Entfernt.",
  "members.remove": "Entfernen",
  "members.roles": "Administratoren laden Personen ein und verwalten Rollen; Entwickler erstellen und bearbeiten Projekte und veröffentlichen Versionen; Betrachter lesen.",
  "members.open": "Offene Einladungen",
  "members.open.none": "Keine offenen Einladungen.",
  "members.until": "bis {date}",
  "members.withdrawn": "Einladung zurückgezogen.",
  "members.withdraw": "Zurückziehen",
  "members.invite": "Jemanden einladen",
  "members.email": "E-Mail-Adresse",
  "members.lang": "Sprache der E-Mail",
  "members.send": "Einladung senden",
  "members.mail.sent": "Die Einladung wurde an {email} gesendet.",
  "members.mail.not": "Die E-Mail wurde nicht gesendet: Geben Sie den Link selbst weiter.",
  "members.link": "Einladungslink (7 Tage gültig):",
  "ops.reinvite.prompt": "E-Mail-Adresse des einzuladenden Administrators:",
  "ops.title": "Organisationen",
  "ops.text": "Alle Organisationen im Builder. Eine neue Organisation erhält ihren ersten Administrator per Einladung; dieser lädt die weiteren ein. Der Builder-Tab der Konsole macht dasselbe.",
  "ops.operator": "Betreiber",
  "ops.on": "Erstellen eingeschaltet",
  "ops.off": "ausgeschaltet",
  "ops.usage": "{projects} Projekt(e)",
  "ops.reinvite": "Administrator einladen",
  "ops.switchoff": "Ausschalten",
  "ops.switchon": "Einschalten",
  "ops.nomembers": "Noch keine Mitglieder.",
  "ops.invited": "eingeladen: {email} ({role}, bis {date})",
  "ops.new": "Neue Organisation",
  "ops.new.text": "Erstellt die Organisation und lädt ihren ersten Administrator ein.",
  "ops.name": "Name",
  "ops.admin": "Erster Administrator (E-Mail)",
  "ops.create": "Erstellen und einladen",
} satisfies Dict<typeof en>;

const it = {
  "members.account.unknown": "Non è stato possibile verificare l'account gIoTHub di questo indirizzo: se la persona non ne ha ancora uno, chieda a Gimasi di crearlo.",
  "members.account.created": "È stato creato un account gIoTHub per questo indirizzo: gIoTHub spiega alla persona per e-mail come configurarlo.",
  "login.drawing": "Disegno di esempio di una scheda elettronica",
  "login.sample": "Disegno di esempio",
  "login.tb.type": "Tipo di dispositivo",
  "login.tb.version": "Versione",
  "login.tb.drawing": "Disegno",
  "login.tb.scale": "Scala",
  "login.lead": "Create tipi di dispositivo con l'aiuto dell'IA: codec, catalogo, guida e test, verificati con il motore del gIoT Store.",
  "login.email": "Indirizzo e-mail",
  "login.go": "Continua",
  "login.bad": "Inserisca il suo indirizzo e-mail.",
  "login.note": "Invitato dalla sua azienda? Apra il link nell'e-mail di invito: la porta qui e la aggiunge alla sua organizzazione. Se non ha ancora un account gIoTHub, un'e-mail separata di gIoTHub le permette di configurarlo.",
  "login.demo": "Demo",
  "noorg.title": "Nessuna organizzazione",
  "noorg.text": "Ha effettuato l'accesso come {email}, ma non è ancora membro di un'organizzazione nel Type Builder.",
  "noorg.invite": "Chieda a un amministratore della sua azienda di invitare questo indirizzo e-mail. L'e-mail di invito contiene un link per entrare.",
  "noorg.disabled": "Per la sua organizzazione la creazione è al momento disattivata.",
  "invite.unknown": "Questo link di invito non è valido.",
  "invite.lead": "È invitato a unirsi a {org} nel gIoTHub Device Type Builder come {role}.",
  "invite.accepted": "Questo invito è già stato accettato. Acceda per continuare.",
  "invite.expired": "Questo invito è scaduto. Ne chieda uno nuovo.",
  "invite.revoked": "Questo invito è stato ritirato.",
  "invite.signin": "Acceda con {email} per accettare.",
  "invite.title": "Unirsi a {org}",
  "invite.from": "Invitato da {who}.",
  "invite.until": "Il link è valido fino al {date}.",
  "invite.wrong": "Ha effettuato l'accesso come {email}, ma l'invito è per {invited}. Esca e acceda con l'indirizzo invitato.",
  "invite.unverified": "Il suo indirizzo e-mail non è ancora verificato nel suo account gIoTHub: lo verifichi prima.",
  "invite.accept": "Accetta l'invito",
  "members.title": "Membri di {org}",
  "members.leave": "Lascia l'organizzazione",
  "members.leave.confirm": "Lasciare {org}? Perderà l'accesso ai suoi progetti.",
  "members.person": "Persona",
  "members.role": "Ruolo",
  "members.you": "lei",
  "members.changed": "Ruolo modificato.",
  "members.remove.confirm": "Rimuovere {who} dall'organizzazione? I suoi progetti restano.",
  "members.removed": "Rimosso.",
  "members.remove": "Rimuovi",
  "members.roles": "Gli amministratori invitano persone e gestiscono i ruoli; gli sviluppatori creano e modificano progetti e pubblicano versioni; i lettori consultano.",
  "members.open": "Inviti aperti",
  "members.open.none": "Nessun invito aperto.",
  "members.until": "fino al {date}",
  "members.withdrawn": "Invito ritirato.",
  "members.withdraw": "Ritira",
  "members.invite": "Invita qualcuno",
  "members.email": "Indirizzo e-mail",
  "members.lang": "Lingua dell'e-mail",
  "members.send": "Invia l'invito",
  "members.mail.sent": "L'invito è stato inviato a {email}.",
  "members.mail.not": "L'e-mail non è stata inviata: trasmetta lei il link.",
  "members.link": "Link di invito (valido 7 giorni):",
  "ops.reinvite.prompt": "Indirizzo e-mail dell'amministratore da invitare:",
  "ops.title": "Organizzazioni",
  "ops.text": "Tutte le organizzazioni del builder. Una nuova organizzazione riceve il suo primo amministratore tramite invito; questi invita gli altri. La scheda Builder della console fa lo stesso.",
  "ops.operator": "operatore",
  "ops.on": "creazione attiva",
  "ops.off": "disattivata",
  "ops.usage": "{projects} progetto/i",
  "ops.reinvite": "Invita un amministratore",
  "ops.switchoff": "Disattiva",
  "ops.switchon": "Attiva",
  "ops.nomembers": "Ancora nessun membro.",
  "ops.invited": "invitato: {email} ({role}, fino al {date})",
  "ops.new": "Nuova organizzazione",
  "ops.new.text": "Crea l'organizzazione e invita il suo primo amministratore.",
  "ops.name": "Nome",
  "ops.admin": "Primo amministratore (e-mail)",
  "ops.create": "Crea e invita",
} satisfies Dict<typeof en>;

const fr = {
  "members.account.unknown": "Le compte gIoTHub de cette adresse n'a pas pu être vérifié : si la personne n'en a pas encore, demandez à Gimasi de le créer.",
  "members.account.created": "Un compte gIoTHub a été créé pour cette adresse : gIoTHub explique par e-mail à la personne comment le configurer.",
  "login.drawing": "Dessin d'exemple d'une carte électronique",
  "login.sample": "Dessin d'exemple",
  "login.tb.type": "Type d'appareil",
  "login.tb.version": "Version",
  "login.tb.drawing": "Dessin",
  "login.tb.scale": "Échelle",
  "login.lead": "Créez des types d'appareils avec l'aide de l'IA : codec, catalogue, guide et tests, vérifiés par le moteur du gIoT Store.",
  "login.email": "Adresse e-mail",
  "login.go": "Continuer",
  "login.bad": "Saisissez votre adresse e-mail.",
  "login.note": "Invité par votre entreprise ? Ouvrez le lien de l'e-mail d'invitation : il vous amène ici et vous ajoute à votre organisation. Si vous n'avez pas encore de compte gIoTHub, un e-mail séparé de gIoTHub vous permet de le configurer.",
  "login.demo": "Démo",
  "noorg.title": "Pas encore d'organisation",
  "noorg.text": "Vous êtes connecté en tant que {email}, mais vous n'êtes encore membre d'aucune organisation dans le Type Builder.",
  "noorg.invite": "Demandez à un administrateur de votre entreprise d'inviter cette adresse e-mail. L'e-mail d'invitation contient un lien pour entrer.",
  "noorg.disabled": "La création est désactivée pour votre organisation pour le moment.",
  "invite.unknown": "Ce lien d'invitation n'est pas valable.",
  "invite.lead": "Vous êtes invité à rejoindre {org} dans le gIoTHub Device Type Builder en tant que {role}.",
  "invite.accepted": "Cette invitation a déjà été acceptée. Connectez-vous pour continuer.",
  "invite.expired": "Cette invitation a expiré. Demandez-en une nouvelle.",
  "invite.revoked": "Cette invitation a été retirée.",
  "invite.signin": "Connectez-vous avec {email} pour l'accepter.",
  "invite.title": "Rejoindre {org}",
  "invite.from": "Invité par {who}.",
  "invite.until": "Le lien est valable jusqu'au {date}.",
  "invite.wrong": "Vous êtes connecté en tant que {email}, mais l'invitation est pour {invited}. Déconnectez-vous et connectez-vous avec l'adresse invitée.",
  "invite.unverified": "Votre adresse e-mail n'est pas encore vérifiée dans votre compte gIoTHub : vérifiez-la d'abord.",
  "invite.accept": "Accepter l'invitation",
  "members.title": "Membres de {org}",
  "members.leave": "Quitter l'organisation",
  "members.leave.confirm": "Quitter {org} ? Vous perdrez l'accès à ses projets.",
  "members.person": "Personne",
  "members.role": "Rôle",
  "members.you": "vous",
  "members.changed": "Rôle modifié.",
  "members.remove.confirm": "Retirer {who} de l'organisation ? Ses projets restent.",
  "members.removed": "Retiré.",
  "members.remove": "Retirer",
  "members.roles": "Les administrateurs invitent des personnes et gèrent les rôles ; les développeurs créent et modifient des projets et publient des versions ; les lecteurs consultent.",
  "members.open": "Invitations ouvertes",
  "members.open.none": "Aucune invitation ouverte.",
  "members.until": "jusqu'au {date}",
  "members.withdrawn": "Invitation retirée.",
  "members.withdraw": "Retirer",
  "members.invite": "Inviter quelqu'un",
  "members.email": "Adresse e-mail",
  "members.lang": "Langue de l'e-mail",
  "members.send": "Envoyer l'invitation",
  "members.mail.sent": "L'invitation a été envoyée à {email}.",
  "members.mail.not": "L'e-mail n'a pas été envoyé : transmettez le lien vous-même.",
  "members.link": "Lien d'invitation (valable 7 jours) :",
  "ops.reinvite.prompt": "Adresse e-mail de l'administrateur à inviter :",
  "ops.title": "Organisations",
  "ops.text": "Toutes les organisations du builder. Une nouvelle organisation reçoit son premier administrateur par invitation ; celui-ci invite les autres. L'onglet Builder de la console fait de même.",
  "ops.operator": "opérateur",
  "ops.on": "création active",
  "ops.off": "désactivée",
  "ops.usage": "{projects} projet(s)",
  "ops.reinvite": "Inviter un administrateur",
  "ops.switchoff": "Désactiver",
  "ops.switchon": "Activer",
  "ops.nomembers": "Pas encore de membres.",
  "ops.invited": "invité : {email} ({role}, jusqu'au {date})",
  "ops.new": "Nouvelle organisation",
  "ops.new.text": "Crée l'organisation et invite son premier administrateur.",
  "ops.name": "Nom",
  "ops.admin": "Premier administrateur (e-mail)",
  "ops.create": "Créer et inviter",
} satisfies Dict<typeof en>;

export default { en, de, it, fr };
